import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, MapPin, Building, Eye, Users, TrendingUp, CheckCircle, Play, Mail, Phone, MessageCircle, Camera, GraduationCap, Gift, ExternalLink, ShieldCheck, Zap, Clock, Search, Star, MousePointerClick } from "lucide-react";

/* ─── Hook contatore animato ─── */
function useCountUp(target, duration = 2000) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useCallback((node) => {
    if (!node) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started) {
        setStarted(true);
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(eased * target));
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.5 });
    obs.observe(node);
  }, [target, duration, started]);
  return [count, ref];
}

/* ─── Stat animata con contatore ─── */
function CounterStat({ icon: Icon, color, target, suffix = "", label }) {
  const [count, ref] = useCountUp(target, 2200);
  return (
    <span ref={ref} className="flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5" style={{ color }} />
      <strong className="text-white tabular-nums">{count.toLocaleString("it-IT")}{suffix}</strong> {label}
    </span>
  );
}

/* ─── Badge Google Street View Trusted (SVG inline) ─── */
function GSVTrustedBadge({ size = "sm" }) {
  const s = size === "sm" ? "w-5 h-5" : "w-7 h-7";
  return (
    <span className="inline-flex items-center gap-1.5" data-testid="gsv-trusted-badge">
      <span className={`${s} rounded-full bg-[#34A853] flex items-center justify-center flex-shrink-0`}>
        <svg viewBox="0 0 16 16" className="w-3 h-3" fill="white"><path d="M13.485 1.929a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06 0l-3.5-3.5a.75.75 0 111.06-1.06L5.44 8.9l6.97-6.97a.75.75 0 011.06 0z"/></svg>
      </span>
      <span className="text-[#34A853] text-xs font-bold whitespace-nowrap">Street View Trusted</span>
    </span>
  );
}

const shortFrames = [
  {
    bg: "bg-gradient-to-b from-[#4285F4]/20 to-[#0c1220]",
    icon: Search,
    iconColor: "#4285F4",
    title: "Il tuo cliente",
    subtitle: "ti cerca su Google...",
    detail: null,
  },
  {
    bg: "bg-gradient-to-b from-[#EA4335]/20 to-[#0c1220]",
    icon: MapPin,
    iconColor: "#EA4335",
    title: "Trova la tua scheda",
    subtitle: "Business Profile",
    detail: "Ma non c'è il Virtual Tour",
  },
  {
    bg: "bg-gradient-to-b from-[#0c1220] to-[#0c1220]",
    icon: null,
    iconColor: null,
    title: null,
    subtitle: null,
    detail: null,
    image: "https://customer-assets.emergentagent.com/job_dc64a694-c406-4567-80bc-477df497ec71/artifacts/puhjn96n_explorer_81asASoCrT.jpg",
    imageCaption: "Colle Val d'Elsa — Visualizzazione immersiva",
  },
  {
    bg: "bg-gradient-to-b from-[#34A853]/20 to-[#0c1220]",
    icon: Camera,
    iconColor: "#34A853",
    title: "Con il Virtual Tour",
    subtitle: "il cliente esplora e prenota",
    detail: "+30% conversioni",
  },
  {
    bg: "bg-gradient-to-b from-[#0c1220] to-[#0c1220]",
    icon: null,
    iconColor: null,
    title: null,
    subtitle: null,
    detail: null,
    image: "https://customer-assets.emergentagent.com/job_dc64a694-c406-4567-80bc-477df497ec71/artifacts/oz3ta1r6_image.png",
    imageCaption: "Crescita profitti fino al 90%",
  },
  {
    bg: "bg-gradient-to-b from-[#FBBC04]/20 to-[#0c1220]",
    icon: ShieldCheck,
    iconColor: "#FBBC04",
    title: "Formazione",
    subtitle: "100% Deducibile",
    detail: "3 rate PayPal · TAEG 0%",
  },
  {
    bg: "bg-gradient-to-b from-[#4285F4]/20 to-[#0c1220]",
    icon: Star,
    iconColor: "#4285F4",
    title: "7.000+ foto",
    subtitle: "52 milioni di views",
    detail: "Fotografo Google Certificato",
  },
  {
    bg: "bg-gradient-to-b from-[#D4AF37]/30 to-[#0c1220]",
    icon: MousePointerClick,
    iconColor: "#D4AF37",
    title: "Contattami ora",
    subtitle: "Posti limitati",
    detail: "schedabusiness.com",
  },
];

function AnimatedShort() {
  const [frame, setFrame] = useState(0);
  const advance = useCallback(() => {
    setFrame((prev) => (prev + 1) % shortFrames.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(advance, 3500);
    return () => clearInterval(timer);
  }, [advance]);

  const f = shortFrames[frame];

  return (
    <div className="w-full h-full relative flex flex-col items-center justify-center cursor-pointer select-none" onClick={advance}>
      <AnimatePresence mode="wait">
        <motion.div
          key={frame}
          className={`absolute inset-0 flex flex-col items-center justify-center px-4 text-center ${f.bg}`}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.4 }}
        >
          {f.image ? (
            <>
              <img src={f.image} alt={f.imageCaption} className="w-full h-3/4 object-cover rounded-xl" />
                <p className="text-base text-[#9CA3AF] mt-2 px-2">{f.imageCaption}</p>
            </>
          ) : (
            <>
              {f.icon && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 300, delay: 0.1 }}
                >
                  <f.icon className="w-10 h-10 mb-3" style={{ color: f.iconColor }} />
                </motion.div>
              )}
              <motion.p
                className="text-lg font-bold text-white"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.15 }}
              >
                {f.title}
              </motion.p>
              <motion.p
                className="text-sm mt-1"
                style={{ color: f.iconColor || "#9CA3AF" }}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.25 }}
              >
                {f.subtitle}
              </motion.p>
              {f.detail && (
                <motion.p
                  className="text-base text-[#9CA3AF] mt-2 px-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  {f.detail}
                </motion.p>
              )}
            </>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Progress dots */}
      <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 z-10">
        {shortFrames.map((_, i) => (
          <div
            key={i}
            className="w-1.5 h-1.5 rounded-full transition-all duration-300"
            style={{ backgroundColor: i === frame ? (shortFrames[frame].iconColor || "#fff") : "rgba(255,255,255,0.2)" }}
          />
        ))}
      </div>
    </div>
  );
}

const tours = [
  {
    id: "poggio",
    name: "Poggio alle Lame",
    subtitle: "Agriturismo & Camere",
    description: "Tour immersivo delle camere e spazi comuni dell'agriturismo toscano",
    image: "/360/poggio/room2.jpg",
    icon: MapPin,
    category: "HO.RE.CA",
    color: "bg-[#EA4335]"
  },
  {
    id: "openstudio",
    name: "Studio Legale",
    subtitle: "Consulenza Professionale",
    description: "Esplora gli interni di uno studio di consulenza professionale",
    image: "/360/openstudio/room2.jpg",
    icon: Building,
    category: "Studi Professionali",
    color: "bg-[#4285F4]"
  }
];

const benefits = [
  {
    icon: Eye,
    title: "Esperienza Immersiva",
    description: "I tuoi clienti esplorano gli spazi come se fossero lì, 24/7, da qualsiasi dispositivo",
    color: "text-[#4285F4] bg-[#4285F4]/10"
  },
  {
    icon: Users,
    title: "Conversioni +40%",
    description: "I virtual tour aumentano il tasso di conversione e riducono le visite a vuoto",
    color: "text-[#34A853] bg-[#34A853]/10"
  },
  {
    icon: TrendingUp,
    title: "Vantaggio Competitivo",
    description: "Distinguiti dalla concorrenza con tecnologia che pochi ancora utilizzano",
    color: "text-[#EA4335] bg-[#EA4335]/10"
  }
];

const stats = [
  { value: "2x", label: "Visualizzazioni Google Maps", color: "text-[#4285F4]" },
  { value: "+42%", label: "Richieste indicazioni", color: "text-[#34A853]" },
  { value: "+35%", label: "Clic al sito web", color: "text-[#FBBC05]" },
  { value: "+130%", label: "Prenotazioni 18-34 anni", color: "text-[#EA4335]" }
];

/* ─── Contatore Live Posti Disponibili ─── */
function LiveSpotsCounter() {
  const spots = useMemo(() => {
    const start = new Date(2026, 1, 1).getTime();
    const now = Date.now();
    const daysPassed = Math.floor((now - start) / (1000 * 60 * 60 * 24));
    const base = 12;
    const taken = Math.min(Math.floor(daysPassed / 3), base - 3);
    return Math.max(base - taken, 3);
  }, []);
  const [displayed, setDisplayed] = useState(spots + 3);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    if (displayed > spots) {
      const timer = setTimeout(() => {
        setDisplayed(d => d - 1);
        setPulse(true);
        setTimeout(() => setPulse(false), 600);
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [displayed, spots]);

  const color = displayed <= 4 ? "#EA4335" : displayed <= 7 ? "#FBBC04" : "#FBBC04";

  return (
    <div className="p-4 rounded-xl bg-white/5 text-center relative overflow-hidden">
      <motion.p
        className="text-3xl font-bold"
        style={{ color }}
        animate={pulse ? { scale: [1, 1.3, 1] } : {}}
        transition={{ duration: 0.4 }}
        data-testid="live-spots-counter"
      >
        {displayed}
      </motion.p>
      <p className="text-sm text-[#9CA3AF]">posti disponibili</p>
      {displayed <= 5 && (
        <motion.div
          className="absolute inset-0 rounded-xl"
          style={{ border: `2px solid ${color}` }}
          animate={{ opacity: [0.3, 0.8, 0.3] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        />
      )}
    </div>
  );
}

/* ─── Badge Google Street View Certificato ─── */
function GoogleBadge({ size = "sm" }) {
  const sizes = {
    sm: "text-xs gap-1.5 px-3 py-1.5",
    md: "text-sm gap-2 px-4 py-2",
  };
  return (
    <div className={`inline-flex items-center rounded-full bg-[#4285F4]/10 border border-[#4285F4]/20 ${sizes[size]}`} data-testid="google-badge">
      <div className="w-5 h-5 rounded-full bg-[#4285F4] flex items-center justify-center flex-shrink-0">
        <MapPin className="w-3 h-3 text-white" />
      </div>
      <span className="text-[#4285F4] font-medium">Fotografo Google Street View Certificato</span>
    </div>
  );
}

/* ─── Testimonianza Card ─── */
function TestimonialCard({ name, location, stars, text, truncate }) {
  const [expanded, setExpanded] = useState(false);
  const maxLen = 120;
  const isLong = truncate && text.length > maxLen;
  const displayText = isLong && !expanded ? text.slice(0, maxLen) + "..." : text;

  return (
    <div className="p-5 rounded-2xl bg-white/5 border border-white/10" data-testid={`testimonial-${name.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex gap-1 mb-2">
        {[...Array(stars)].map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-[#FBBC04] text-[#FBBC04]" />
        ))}
      </div>
      <p className="text-white text-sm leading-relaxed mb-3">"{displayText}"</p>
      {isLong && !expanded && (
        <button onClick={() => setExpanded(true)} className="text-[#D4AF37] text-xs font-bold hover:underline mb-3">
          continua a leggere
        </button>
      )}
      <div className="border-t border-white/10 pt-3">
        <p className="text-white font-bold text-sm">{name}</p>
        <p className="text-gray-500 text-xs">{location}</p>
      </div>
    </div>
  );
}

/* ─── Scroll-to-Top Button ─── */
function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-6 left-6 z-50 w-12 h-12 rounded-full bg-black border-2 border-[#25D366] shadow-lg hover:shadow-xl transition-all flex items-center justify-center opacity-50 hover:opacity-100"
          data-testid="scroll-to-top"
          aria-label="Torna in alto"
        >
          <ArrowRight className="w-5 h-5 text-[#25D366] rotate-[-90deg]" />
        </motion.button>
      )}
    </AnimatePresence>
  );
}

/* ─── Logo ORO Strip — Riutilizzabile tra sezioni ─── */
const LogoStrip = () => (
  <div className="py-8 bg-[#0a0e17] border-y border-white/5">
    <motion.div
      className="flex flex-col items-center gap-1"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
    >
      <p className="text-2xl sm:text-3xl text-[#D4AF37]" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 900 }}>schedabusiness.com</p>
      <p className="text-sm italic" style={{ color: "#E6E1D2", fontFamily: "Poppins, sans-serif", fontWeight: 700, textShadow: "0 0 12px rgba(255,255,255,0.5)" }}>se non ti trovano, non esisti</p>
    </motion.div>
  </div>
);

/* ─── CTA PayPal Strip — compatta, tra le sezioni ─── */
const CTAFormazione = () => (
  <div className="py-4 bg-[#0c1220]">
    <motion.div
      className="max-w-3xl mx-auto px-4"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
    >
      <a href="#pacchetti" className="flex items-center justify-center gap-3 sm:gap-4 flex-wrap py-3 px-5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/8 transition-all group">
        <svg width="70" height="18" viewBox="0 0 169 44" className="flex-shrink-0">
          <path d="M27.2 3.6h-9.8c-.7 0-1.2.5-1.3 1.1l-4 25.1c-.1.5.3.9.8.9h4.7c.7 0 1.2-.5 1.3-1.1l1.1-6.8c.1-.6.7-1.1 1.3-1.1h3.1c6.4 0 10.2-3.1 11.1-9.3.4-2.7 0-4.8-1.2-6.3C32.8 4.4 30.4 3.6 27.2 3.6zm1.1 9.2c-.5 3.5-3.2 3.5-5.8 3.5H21l1-6.5c.1-.4.4-.7.8-.7h.7c1.8 0 3.4 0 4.3 1 .5.6.7 1.5.5 2.7z" fill="#003087"/>
          <path d="M55.4 12.7h-4.7c-.4 0-.7.3-.8.7l-.2 1.3-.3-.5c-1-1.5-3.3-2-5.5-2-5.2 0-9.6 3.9-10.5 9.4-.4 2.7.2 5.4 1.7 7.2 1.4 1.7 3.5 2.4 5.9 2.4 4.2 0 6.5-2.7 6.5-2.7l-.2 1.3c-.1.5.3.9.8.9h4.2c.7 0 1.2-.5 1.3-1.1l2.5-16c.1-.4-.3-.9-.7-.9zm-6.5 9.1c-.5 2.7-2.6 4.5-5.3 4.5-1.4 0-2.4-.4-3.1-1.3-.7-.8-1-2-.7-3.3.4-2.7 2.6-4.5 5.3-4.5 1.3 0 2.4.5 3.1 1.3.7.8 1 2 .8 3.3z" fill="#003087"/>
          <path d="M82.7 12.7h-4.7c-.4 0-.9.2-1.1.6l-6.5 9.6-2.8-9.2c-.2-.5-.6-.9-1.3-.9h-4.6c-.5 0-.9.5-.7 1.1l5.2 15.3-4.9 6.9c-.4.5 0 1.2.6 1.2h4.7c.4 0 .9-.2 1.1-.6l15.7-22.7c.3-.5-.1-1.3-.7-1.3z" fill="#003087"/>
          <path d="M102.2 3.6h-9.8c-.7 0-1.2.5-1.3 1.1l-4 25.1c-.1.5.3.9.8.9h5c.5 0 .9-.3.9-.8l1.1-7.1c.1-.6.7-1.1 1.3-1.1h3.1c6.4 0 10.2-3.1 11.1-9.3.4-2.7 0-4.8-1.2-6.3-1.5-1.7-3.9-2.5-7-2.5zm1.1 9.2c-.5 3.5-3.2 3.5-5.8 3.5h-1.5l1-6.5c.1-.4.4-.7.8-.7h.7c1.8 0 3.4 0 4.3 1 .5.6.7 1.5.5 2.7z" fill="#0070BA"/>
          <path d="M130.4 12.7h-4.7c-.4 0-.7.3-.8.7l-.2 1.3-.3-.5c-1-1.5-3.3-2-5.5-2-5.2 0-9.6 3.9-10.5 9.4-.4 2.7.2 5.4 1.7 7.2 1.4 1.7 3.5 2.4 5.9 2.4 4.2 0 6.5-2.7 6.5-2.7l-.2 1.3c-.1.5.3.9.8.9h4.2c.7 0 1.2-.5 1.3-1.1l2.5-16c.1-.4-.3-.9-.7-.9zm-6.5 9.1c-.5 2.7-2.6 4.5-5.3 4.5-1.4 0-2.4-.4-3.1-1.3-.7-.8-1-2-.7-3.3.4-2.7 2.6-4.5 5.3-4.5 1.3 0 2.4.5 3.1 1.3.7.8 1 2 .8 3.3z" fill="#0070BA"/>
          <path d="M152.5 4.2l-4 25.6c-.1.5.3.9.8.9h4c.7 0 1.2-.5 1.3-1.1l4-25.1c.1-.5-.3-.9-.8-.9h-4.5c-.4 0-.7.2-.8.6z" fill="#0070BA"/>
        </svg>
        <span className="text-[#9CA3AF] text-sm">paga 3 rate</span>
        <span className="text-[#EA4335] line-through text-sm">€299</span>
        <span className="text-[#D4AF37] font-black text-lg" style={{ textShadow: "0 0 10px rgba(212,175,55,0.5)" }}>€249</span>
        <span className="text-white/60 text-sm">€99,67 x 3</span>
        <span className="text-[#9CA3AF] text-xs">TAEG 0%</span>
        <span className="text-[#34A853] font-bold text-xs">Deducibile 100%</span>
      </a>
    </motion.div>
  </div>
);

/* ─── Bundle "Acquistali Entrambi" — solo 1 volta dopo i pacchetti ─── */
const BundleSection = () => (
  <div className="py-6 bg-[#0c1220]">
    <motion.div
      className="max-w-3xl mx-auto px-4"
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <div className="relative p-[2px] rounded-2xl" style={{ background: "linear-gradient(90deg, #EA4335, #FBBC05, #34A853, #4285F4, #EA4335, #FBBC05)" }}>
        <a href="#pacchetti" className="block p-5 rounded-2xl bg-[#0c1220] hover:bg-[#0f1629] transition-all cursor-pointer group">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FBBC05]/20 flex items-center justify-center flex-shrink-0">
                <Gift className="w-5 h-5 text-[#FBBC05]" />
              </div>
              <div>
                <p className="text-white font-bold text-base">Acquistali entrambi: conviene!</p>
                <p className="text-[#9CA3AF] text-sm">
                  Virtual Tour + Formazione · <span className="line-through text-[#EA4335]">€299</span>{" "}
                  <span className="text-[#D4AF37] font-black text-lg" style={{ textShadow: "0 0 12px rgba(212,175,55,0.6), 0 0 24px rgba(212,175,55,0.3)" }}>€249</span>
                </p>
              </div>
            </div>
            <span className="flex-shrink-0 px-5 py-2.5 rounded-full bg-[#FBBC05] text-black font-bold text-sm group-hover:bg-[#e5ab04] transition-colors whitespace-nowrap">
              €249
            </span>
          </div>
          <div className="mt-3 pt-3 border-t border-white/10">
            <p className="text-center">
              <span className="text-[#FBBC05] font-black text-base tracking-wide" style={{ textShadow: "0 0 10px rgba(212,175,55,0.5)" }}>GIFT CARD €50 REGALO</span>
              <span className="text-white font-medium text-sm"> di benvenuto: </span>
              <span className="inline-flex text-white font-black text-sm">
                {"clicca qui per riceverlo".split("").map((char, i) => (
                  <span key={i} className="inline-block" style={{ animation: `sb-wave 1.5s ease-in-out ${i * 0.06}s infinite` }}>
                    {char === " " ? "\u00A0" : char}
                  </span>
                ))}
              </span>
            </p>
          </div>
          <div className="flex items-center justify-center gap-4 mt-3 pt-3 border-t border-white/5">
            <a href="https://accounts.google.com" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="w-9 h-9 rounded-lg bg-white flex items-center justify-center hover:scale-110 transition-transform" title="Google">
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            </a>
            <a href="https://www.paypal.com/myaccount/transfer/" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="h-9 px-2 rounded-lg bg-white flex items-center justify-center hover:scale-110 transition-transform" title="PayPal">
              <svg width="70" height="20" viewBox="0 0 169 44"><path d="M27.2 3.6h-9.8c-.7 0-1.2.5-1.3 1.1l-4 25.1c-.1.5.3.9.8.9h4.7c.7 0 1.2-.5 1.3-1.1l1.1-6.8c.1-.6.7-1.1 1.3-1.1h3.1c6.4 0 10.2-3.1 11.1-9.3.4-2.7 0-4.8-1.2-6.3C32.8 4.4 30.4 3.6 27.2 3.6zm1.1 9.2c-.5 3.5-3.2 3.5-5.8 3.5H21l1-6.5c.1-.4.4-.7.8-.7h.7c1.8 0 3.4 0 4.3 1 .5.6.7 1.5.5 2.7z" fill="#003087"/><path d="M55.4 12.7h-4.7c-.4 0-.7.3-.8.7l-.2 1.3-.3-.5c-1-1.5-3.3-2-5.5-2-5.2 0-9.6 3.9-10.5 9.4-.4 2.7.2 5.4 1.7 7.2 1.4 1.7 3.5 2.4 5.9 2.4 4.2 0 6.5-2.7 6.5-2.7l-.2 1.3c-.1.5.3.9.8.9h4.2c.7 0 1.2-.5 1.3-1.1l2.5-16c.1-.4-.3-.9-.7-.9zm-6.5 9.1c-.5 2.7-2.6 4.5-5.3 4.5-1.4 0-2.4-.4-3.1-1.3-.7-.8-1-2-.7-3.3.4-2.7 2.6-4.5 5.3-4.5 1.3 0 2.4.5 3.1 1.3.7.8 1 2 .8 3.3z" fill="#003087"/><path d="M82.7 12.7h-4.7c-.4 0-.9.2-1.1.6l-6.5 9.6-2.8-9.2c-.2-.5-.6-.9-1.3-.9h-4.6c-.5 0-.9.5-.7 1.1l5.2 15.3-4.9 6.9c-.4.5 0 1.2.6 1.2h4.7c.4 0 .9-.2 1.1-.6l15.7-22.7c.3-.5-.1-1.3-.7-1.3z" fill="#003087"/><path d="M102.2 3.6h-9.8c-.7 0-1.2.5-1.3 1.1l-4 25.1c-.1.5.3.9.8.9h5c.5 0 .9-.3.9-.8l1.1-7.1c.1-.6.7-1.1 1.3-1.1h3.1c6.4 0 10.2-3.1 11.1-9.3.4-2.7 0-4.8-1.2-6.3-1.5-1.7-3.9-2.5-7-2.5zm1.1 9.2c-.5 3.5-3.2 3.5-5.8 3.5h-1.5l1-6.5c.1-.4.4-.7.8-.7h.7c1.8 0 3.4 0 4.3 1 .5.6.7 1.5.5 2.7z" fill="#0070BA"/><path d="M130.4 12.7h-4.7c-.4 0-.7.3-.8.7l-.2 1.3-.3-.5c-1-1.5-3.3-2-5.5-2-5.2 0-9.6 3.9-10.5 9.4-.4 2.7.2 5.4 1.7 7.2 1.4 1.7 3.5 2.4 5.9 2.4 4.2 0 6.5-2.7 6.5-2.7l-.2 1.3c-.1.5.3.9.8.9h4.2c.7 0 1.2-.5 1.3-1.1l2.5-16c.1-.4-.3-.9-.7-.9zm-6.5 9.1c-.5 2.7-2.6 4.5-5.3 4.5-1.4 0-2.4-.4-3.1-1.3-.7-.8-1-2-.7-3.3.4-2.7 2.6-4.5 5.3-4.5 1.3 0 2.4.5 3.1 1.3.7.8 1 2 .8 3.3z" fill="#0070BA"/><path d="M152.5 4.2l-4 25.6c-.1.5.3.9.8.9h4c.7 0 1.2-.5 1.3-1.1l4-25.1c.1-.5-.3-.9-.8-.9h-4.5c-.4 0-.7.2-.8.6z" fill="#0070BA"/></svg>
            </a>
            <a href="https://appleid.apple.com" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="w-9 h-9 rounded-lg bg-white flex items-center justify-center hover:scale-110 transition-transform" title="Apple">
              <svg width="16" height="20" viewBox="0 0 17 21" fill="#000"><path d="M13.545 11.2c-.02-2.097 1.711-3.103 1.79-3.152-1.008-1.477-2.558-1.667-3.088-1.702-1.294-.137-2.56.782-3.22.782-.674 0-1.685-.77-2.786-.748-1.403.023-2.727.843-3.449 2.11-1.493 2.594-.38 6.413 1.053 8.517.72 1.03 1.562 2.18 2.662 2.14 1.081-.044 1.487-.692 2.793-.692 1.294 0 1.68.692 2.798.666 1.152-.019 1.878-1.034 2.577-2.073.832-1.183 1.165-2.353 1.18-2.415-.027-.009-2.252-.862-2.272-3.433zm-2.137-6.31c.573-.728.97-1.72.861-2.728-.837.037-1.895.588-2.493 1.3-.528.628-.999 1.655-.877 2.62.944.072 1.913-.479 2.509-1.192z"/></svg>
            </a>
            <a href="https://www.amazon.it/ap/signin" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="w-9 h-9 rounded-lg bg-[#232F3E] flex items-center justify-center hover:scale-110 transition-transform" title="Amazon">
              <svg width="20" height="16" viewBox="0 0 48 32" fill="none"><path d="M27.5 22.8c-5.3 3.9-13 6-19.6 6-9.3 0-17.6-3.4-23.9-9.1-.5-.5-.1-1.1.5-.7 6.8 4 15.2 6.3 23.9 6.3 5.9 0 12.3-1.2 18.2-3.7.9-.4 1.6.6.9 1.2z" transform="translate(16 2)" fill="#FF9900"/><path d="M29.5 20.5c-.7-.9-4.4-.4-6.1-.2-.5.1-.6-.4-.1-.7 3-2.1 7.9-1.5 8.4-.8.6.7-.2 5.8-2.9 8.2-.4.4-.8.2-.6-.3.6-1.5 2-4.8 1.3-6.2z" transform="translate(16 2)" fill="#FF9900"/><path d="M23.6 4V1.5c0-.4.3-.6.6-.6h11.2c.4 0 .6.3.6.6v2.1c0 .4-.3.8-.8 1.5l-5.8 8.3c2.2-.1 4.4.3 6.4 1.3.4.2.6.6.6 1v2.7c0 .4-.4.8-.8.6-3.4-1.8-7.9-2-11.7.1-.4.2-.8-.2-.8-.6v-2.6c0-.4 0-1.1.4-1.7l6.7-9.6h-5.8c-.4 0-.6-.3-.6-.6z" transform="translate(16 2)" fill="#232F3E"/></svg>
            </a>
            <a href="https://play.google.com" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="w-9 h-9 rounded-lg bg-[#3DDC84]/20 flex items-center justify-center hover:scale-110 transition-transform" title="Android">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#3DDC84"><path d="M17.523 15.341a.998.998 0 0 0 .999-.998v-6.13a.999.999 0 1 0-1.998 0v6.13a.998.998 0 0 0 .999.998zm-11.046 0a.998.998 0 0 0 .999-.998v-6.13a.999.999 0 1 0-1.998 0v6.13a.998.998 0 0 0 .999.998zm-3.479-.998V8.213a.999.999 0 1 0-1.998 0v6.13a.999.999 0 1 0 1.998 0zm17.004 0V8.213a.999.999 0 1 0-1.998 0v6.13a.999.999 0 1 0 1.998 0zM7.998 16.84c0 .55.45 1 .999 1h1v2.16a.999.999 0 1 0 1.998 0v-2.16h2.006v2.16a.999.999 0 1 0 1.998 0v-2.16h1c.55 0 .999-.45.999-1V8.213H7.998v8.627zm7.005-13.358l1.1-1.999a.228.228 0 0 0-.084-.31.229.229 0 0 0-.312.084l-1.114 2.02A7.026 7.026 0 0 0 12 2.782c-.94 0-1.833.178-2.593.495L8.293 1.257a.228.228 0 0 0-.312-.084.229.229 0 0 0-.084.31l1.1 1.999C7.227 4.393 6.078 6.17 5.998 8.213h12.004c-.08-2.043-1.229-3.82-2.999-4.731zM10 6.213a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1zm4 0a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1z"/></svg>
            </a>
            <a href="https://mail.google.com" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="w-9 h-9 rounded-lg bg-white flex items-center justify-center hover:scale-110 transition-transform" title="Gmail">
              <svg width="18" height="14" viewBox="0 0 24 18"><path fill="#4285F4" d="M2 18h4V8l-5-4v13c0 .6.4 1 1 1z"/><path fill="#34A853" d="M18 18h4c.5 0 1-.4 1-1V4l-5 4z"/><path fill="#FBBC05" d="M18 2v6l5-4V3c0-1.2-1.4-1.9-2.4-1.2z"/><path fill="#EA4335" d="M6 8V2l6 4.5L18 2v6l-6 4.5z"/><path fill="#C5221F" d="M1 3v1l5 4V2L3.6 1.3C2.5 0.6 1 1.3 1 3z"/></svg>
            </a>
          </div>
        </a>
      </div>
    </motion.div>
  </div>
);

export default function LandingPage() {
  const navigate = useNavigate();
  const [clickedPkg, setClickedPkg] = useState(null);
  const [hoveredSlice, setHoveredSlice] = useState(null);

  const handlePackageClick = (pkgName, url) => {
    setClickedPkg(pkgName);
    setTimeout(() => {
      window.open(url, '_blank');
      setClickedPkg(null);
    }, 450);
  };

  return (
    <div className="min-h-screen bg-[#0c1220]" data-testid="landing-page">

      {/* Top Emergency Bar */}
      <div className="bg-[#0c1220] text-white py-2.5 px-4 text-center text-sm">
        <span className="text-[#9AA0A6]">Profilo sospeso? Scheda bloccata? </span>
        <a href="https://wa.me/+393273839782?text=Ciao%20Riccardo!%20Ho%20un%20problema%20con%20la%20mia%20scheda%20Google" target="_blank" rel="noopener noreferrer" className="text-[#4285F4] font-semibold hover:underline">
          Chiamami, ci penso io
        </a>
        <span className="text-[#9AA0A6] mx-2">|</span>
        <span className="text-[#34A853] font-medium">100% Deducibile</span>
        <span className="text-[#9AA0A6] mx-2">·</span>
        <span className="text-[#FBBC05] font-medium">TAEG 0%</span>
        <span className="text-[#9AA0A6] mx-2">·</span>
        <span className="text-[#EA4335] font-medium">3 Rate PayPal</span>
      </div>

      {/* Navbar */}
      <nav className="sticky top-0 z-40 bg-[#0c1220]/95 backdrop-blur-md border-b border-white/10 px-6 py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="/" className="flex flex-col">
            <span className="text-[#D4AF37] text-xl" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 900 }} data-testid="logo-oro-navbar">schedabusiness.com</span>
            <span className="text-[10px] italic" style={{ color: "#E6E1D2", fontFamily: "Poppins, sans-serif", fontWeight: 700 }}>se non ti trovano, non esisti</span>
          </a>
          <div className="hidden md:flex items-center gap-6 text-sm">
            <button onClick={() => document.getElementById('pacchetti')?.scrollIntoView({ behavior: 'smooth' })} className="text-[#9CA3AF] hover:text-white transition-colors">
              Pacchetti
            </button>
            <button onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' })} className="text-[#9CA3AF] hover:text-white transition-colors">
              Demo 360°
            </button>
            <button onClick={() => document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' })} className="text-[#9CA3AF] hover:text-white transition-colors">
              FAQ
            </button>
            <a href="https://www.schedabusiness.com" target="_blank" rel="noopener noreferrer" className="text-[#9CA3AF] hover:text-white transition-colors">
              Assistenza & Pacchetti
            </a>
          </div>
          {/* Cerchio azzurro Detraibile 100% che ruota + Badge GSV */}
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex"><GSVTrustedBadge /></div>
            <button
              onClick={() => document.getElementById('deducibile')?.scrollIntoView({ behavior: 'smooth' })}
              className="relative w-14 h-14 flex-shrink-0"
              data-testid="cta-detraibile-circle"
            >
            <motion.div
              className="absolute inset-0 rounded-full border-2 border-dashed border-white/40"
              animate={{ rotate: 360 }}
              transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
            />
            <div className="absolute inset-1 rounded-full bg-[#4285F4] flex items-center justify-center shadow-lg hover:bg-[#3b78e7] transition-colors">
              <span className="text-white font-black text-[10px] leading-tight text-center" style={{ fontFamily: "Poppins, sans-serif" }}>
                Detraibile<br/>100%
              </span>
            </div>
          </button>
          </div>
        </div>
      </nav>
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-b from-[#0c1220] to-[#111827]">
        <div className="relative z-10 max-w-5xl mx-auto px-6 py-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Badge - moved below quote */}

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white mb-4 font-semibold leading-tight">
              La Tua Scheda Google
              <br />
              <span className="text-[#4285F4]">Merita</span>{" "}
              <span className="text-[#EA4335]">di Più</span>
            </h1>
            <h2 className="text-xl sm:text-2xl text-[#9CA3AF] mb-6 font-medium leading-snug max-w-3xl mx-auto">
              Assistenza tecnica, Virtual Tour 360° e formazione dedicata per il tuo profilo aziendale su{" "}
              <span className="text-[#4285F4]">G</span>
              <span className="text-[#EA4335]">o</span>
              <span className="text-[#FBBC05]">o</span>
              <span className="text-[#4285F4]">g</span>
              <span className="text-[#34A853]">l</span>
              <span className="text-[#EA4335]">e</span>
              {" "}Maps. <strong className="text-white">Ci penso io.</strong>
            </h2>

            {/* Visual Grid — Immagini + Video Short */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto mb-8">
              {/* Puzzle Logo */}
              <motion.div
                className="rounded-2xl overflow-hidden border border-white/10 bg-white/5 p-4 flex flex-col items-center justify-center gap-3"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/dc64a694-c406-4567-80bc-477df497ec71/images/1f962bbe6b14d9c8cf865c55d9f9623c16150be00adf703589af821f10f32c23.png"
                  alt="schedabusiness.com puzzle logo"
                  className="w-full max-w-[180px] object-contain"
                  data-testid="hero-puzzle-img"
                />
              </motion.div>

              {/* Animated Short — 20s storytelling */}
              <motion.div
                className="rounded-2xl overflow-hidden border border-white/10 bg-[#0c1220] relative"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                style={{ aspectRatio: '9/16', maxHeight: '420px' }}
                data-testid="hero-short"
              >
                <AnimatedShort />
              </motion.div>

              {/* Analytics Image */}
              <motion.div
                className="rounded-2xl overflow-hidden border border-white/10 bg-white/5 p-4 flex items-center justify-center"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_dc64a694-c406-4567-80bc-477df497ec71/artifacts/oz3ta1r6_image.png"
                  alt="Crescita business Google - analytics e conversioni"
                  className="w-full max-w-[220px] object-contain"
                  data-testid="hero-analytics-img"
                />
              </motion.div>
            </div>

            {/* AI Overview Quote */}
            <div className="max-w-3xl mx-auto mb-6 p-5 rounded-xl bg-[#4285F4]/10 border border-[#4285F4]/15">
              <p className="text-[#9CA3AF] text-sm sm:text-base md:text-lg leading-relaxed italic">
                "Un'<strong className="text-[#4285F4]">esperienza</strong> <strong className="text-[#EA4335]">digitale</strong> <strong className="text-google-yellow">appagante</strong> è oggi il <strong className="text-white">principale motore di conversione</strong> da utente a <strong className="text-white">cliente pagante</strong>. 
                Investire nella <strong className="text-[#4285F4]">Customer</strong> <strong className="text-[#34A853]">Experience</strong> <strong className="text-[#EA4335]">Digitale</strong> può <strong className="text-[#34A853]">aumentare la soddisfazione del cliente dal 20% al 30%</strong>, 
                portando a una <strong className="text-[#EA4335]">crescita dei profitti dal 20% al 50%</strong>."
              </p>
              <p className="text-sm text-[#9AA0A6] mt-2">Fonte: AI Overview — Quadient — Andrea Farinet</p>
            </div>

            {/* Badge Google Certificato */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#4285F4]/20 bg-[#4285F4]/10 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#34A853]" />
              <span className="text-[#4285F4] text-sm font-medium">Fotografo Google Street View Certificato dal 2016</span>
            </div>

            {/* CTA Buttons — orizzontali e compatte */}
            <div className="flex gap-3 justify-center items-center flex-wrap">
              <motion.button 
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#4285F4] text-white font-bold text-sm hover:bg-[#3b78e7] transition-colors shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => document.getElementById('demo').scrollIntoView({ behavior: 'smooth' })}
                data-testid="see-demo-btn"
              >
                <Play className="w-4 h-4" /> Demo 360°
              </motion.button>
              <motion.button 
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 text-white font-bold text-sm border border-white/20 hover:bg-white/15 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => document.getElementById('pacchetti').scrollIntoView({ behavior: 'smooth' })}
                data-testid="demo-btn"
              >
                Pacchetti <ArrowRight className="w-4 h-4" />
              </motion.button>
              <motion.a
                href="https://wa.me/+393273839782?text=Ciao%20Riccardo!%20Sono%20interessato%20al%20Virtual%20Tour%20360°"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#25D366] text-white font-bold text-sm hover:bg-[#20BD5A] transition-colors shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                data-testid="cta-whatsapp-hero"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </motion.a>
            </div>

            {/* Badge + Stats sotto CTA */}
            <div className="mt-8 flex flex-col items-center gap-3">
              <div className="flex flex-wrap justify-center items-center gap-3">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#4285F4]/20 bg-[#4285F4]/10">
                  <span className="w-2 h-2 rounded-full bg-[#34A853]" />
                  <span className="text-[#4285F4] text-sm font-medium">Fotografo Google Street View Certificato</span>
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#202124]/10 bg-white/5">
                  <Camera className="w-3 h-3 text-white" />
                  <span className="text-white text-sm font-medium">GoPro Certified</span>
                </div>
              </div>
              <div className="flex flex-wrap justify-center items-center gap-4 text-base text-[#9CA3AF]" data-testid="social-proof-counter">
                <CounterStat icon={Camera} color="#4285F4" target={7000} suffix="+" label="foto pubblicate" />
                <span className="text-[#DADCE0]">|</span>
                <CounterStat icon={Eye} color="#EA4335" target={52} suffix="M" label="di views" />
                <span className="text-[#DADCE0]">|</span>
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-[#34A853]" />
                  su <strong className="text-white">Google Maps</strong> dal 2016
                </span>
              </div>

              {/* Riflessione per il cliente — Closer PNL */}
              <div className="max-w-xl mx-auto mt-4 p-6 rounded-2xl bg-white/5 border border-white/10">
                <p className="text-white text-base sm:text-lg leading-relaxed text-center font-medium">
                  <strong>Fermati un secondo.</strong> Pensaci bene.
                </p>
                <p className="text-[#9CA3AF] text-base leading-relaxed text-center mt-3">
                  Cosa fai <strong className="text-white">tu</strong> prima di scegliere un ristorante, un hotel, un professionista? 
                  Guardi le <strong className="text-white">foto</strong>, leggi le <strong className="text-white">recensioni</strong>, 
                  controlli il <strong className="text-white">sito</strong>. Se trovi un <strong className="text-[#4285F4]">Virtual Tour a 360°</strong>, 
                  ci entri dentro. <strong className="text-white">E poi prenoti.</strong>
                </p>
                <p className="text-white text-base sm:text-lg font-semibold text-center mt-4">
                  I tuoi futuri clienti fanno esattamente lo stesso.
                </p>
                <p className="text-[#9CA3AF] text-base text-center mt-3">
                  Ogni cliente pagante nasce come un utente che cerca su Google. 
                  <strong className="text-white"> La differenza?</strong> Chi si fa trovare con un'<strong className="text-[#4285F4]">esperienza</strong> <strong className="text-[#EA4335]">digitale</strong> <strong className="text-google-yellow">impeccabile</strong>... 
                  <strong className="text-[#34A853]">chiude.</strong>
                </p>
                <p className="text-white text-center mt-4 text-lg font-semibold">
                  Io ti aiuto a chiudere. Punto.
                </p>
              </div>
            </div>

            {/* WhatsApp CTA */}
            <div className="mt-6">
              <a 
                href="https://wa.me/+393273839782?text=Ciao%20Riccardo!%20Sono%20interessato%20al%20Virtual%20Tour%20360°"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#34A853] hover:underline text-sm font-medium"
              >
                <MessageCircle className="w-4 h-4" />
                Oppure scrivimi su WhatsApp
              </a>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            {stats.map((stat, i) => (
              <div key={i} className="text-center p-4 rounded-2xl bg-white/5 shadow-sm border border-white/10">
                <div className={`text-3xl font-semibold mb-1 ${stat.color}`}>{stat.value}</div>
                <div className="text-base text-[#9CA3AF]">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Puzzle Colle Val d'Elsa — Visual break */}
      <section className="py-12 px-6 bg-[#0c1220]">
        <div className="max-w-3xl mx-auto">
          <motion.div
            className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_dc64a694-c406-4567-80bc-477df497ec71/artifacts/5iq0lb5l_chrome_mc1PioHhks.png"
              alt="Colle Val d'Elsa — Puzzle schedabusiness Google Maps"
              className="w-full object-cover"
              data-testid="puzzle-colle-valdelsa"
            />
          </motion.div>
          {/* Logo Ufficiale — identico alla navbar */}
          <a href="/" className="flex flex-col items-center gap-0.5 mt-5" data-testid="logo-oro-puzzle">
            <span className="text-[#D4AF37] text-xl" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 900 }}>schedabusiness.com</span>
            <span className="text-[10px] italic" style={{ color: "#E6E1D2", fontFamily: "Poppins, sans-serif", fontWeight: 700 }}>se non ti trovano, non esisti</span>
          </a>
        </div>
      </section>

      {/* Loghi Clienti — Carosello 1 Riga + Testimonianze (MEDIO-ALTO) */}
      <section className="py-12 px-6 bg-[#0a0e17] overflow-hidden" data-testid="client-logos-section">
        <div className="max-w-5xl mx-auto text-center mb-8">
          <span className="inline-block px-4 py-1 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] text-sm font-medium mb-3">
            Hanno scelto schedabusiness
          </span>
          <h2 className="text-2xl sm:text-3xl text-white font-semibold">
            I nostri <span className="text-[#D4AF37]">clienti</span> parlano per noi
          </h2>
        </div>
        {/* 1 sola riga scorrevole lenta */}
        <div className="relative overflow-hidden">
          <div
            className="flex gap-10 items-center"
            style={{
              animation: 'marquee 45s linear infinite',
              width: 'max-content',
              willChange: 'transform',
            }}
          >
            {[...Array(2)].flatMap(() => [
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/w7zcc6t9_image.png", alt: "Buekke", style: {} },
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/fg2iby93_image.png", alt: "L'Angolo di Sapia", style: { filter: "brightness(1.6)" } },
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/72dc4552_image.png", alt: "Le Grazie Ristorante", style: { filter: "brightness(1.6)" } },
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/g2pqbnr8_image.png", alt: "Crescendo", style: { objectFit: "cover", clipPath: "inset(20% 10% 20% 10%)" } },
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/qvxb1zy3_image.png", alt: "UIL Toscana", style: { objectFit: "cover", clipPath: "inset(22% 0 22% 0)" } },
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/8zqxzjto_image.png", alt: "Spazio 847", style: {} },
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/r6yclocm_image.png", alt: "Le Undici Lune", style: { filter: "brightness(1.6)" }, label: "Le Undici Lune" },
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/c8fd2nv6_image.png", alt: "Al Canto Divino", style: {} },
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/629xzr7m_image.png", alt: "Alimentari Sandra", style: { filter: "brightness(2)" }, label: "Alimentari Sandra" },
              { src: "", alt: "Palazzo Corboli Asciano", style: {}, label: "Palazzo Corboli\nAsciano", textOnly: true },
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/pb1eho8l_image.png", alt: "Misericordia", style: {}, label: "Misericordia" },
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/gj4cvrbo_image.png", alt: "Filo di Arianna", style: {} },
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/blhu2wwd_image.png", alt: "Mario Barone", style: {} },
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/yvml0jtk_image.png", alt: "La Scossa", style: {} },
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/wrnbzitx_image.png", alt: "Antico Borgo Poggiarello", style: {} },
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/j4d92obh_image.png", alt: "Luna di Saturno", style: {} },
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/sz95fb44_explorer_rVsrw3AAQf.png", alt: "Armeria Capitani", style: {} },
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/qxl11jod_image.png", alt: "La Corte dei Gufi", style: {} },
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/3tp5seos_image.png", alt: "Pandora", style: {} },
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/3q72r0uz_image.png", alt: "La Sartoria Tattoo", style: { filter: "brightness(1.3)" } },
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/bloj9g0u_chrome_0fO3E7oxIX.png", alt: "Bar Rubino", style: {}, size: "xl" },
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/3kq69xxa_chrome_KTCygcMEQp.png", alt: "Ottica Valdelsa", style: {}, size: "xl" },
              { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/qo4pi217_chrome_8w3NGXAdbB.png", alt: "C.A.L.M.A.", style: { filter: "brightness(1.5)" } },
            ]).map((logo, i) => (
              <div key={i} className={`flex-shrink-0 ${logo.size === 'xl' ? 'w-36 h-36' : logo.size === 'lg' ? 'w-28 h-28' : 'w-20 h-20'} rounded-xl overflow-hidden flex items-center justify-center relative ${logo.textOnly ? 'bg-black' : ''}`}>
                {logo.textOnly ? (
                  <span className="text-white font-bold text-[10px] text-center leading-tight px-1" style={{ fontFamily: "Poppins, sans-serif" }}>{logo.alt}</span>
                ) : (
                  <>
                    <img src={logo.src} alt={logo.alt} className="w-full h-full object-contain rounded" style={logo.style} loading="lazy" />
                    {logo.label && (
                      <span className="absolute bottom-0 inset-x-0 text-center text-[8px] font-bold text-white bg-black/60 py-0.5 leading-tight">{logo.label}</span>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
        {/* CTA Recensione + Testimonianze */}
        <div className="text-center mt-8">
          <a href="https://g.page/r/CW-WqgxqiG1NEBM/review" target="_blank" rel="noopener noreferrer">
            <motion.button
              className="px-5 py-2 rounded-full bg-[#D4AF37] text-black font-bold text-sm hover:bg-[#d4a518] transition-colors shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="cta-scrivi-recensione"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              Scrivi una Recensione
            </motion.button>
          </a>
          <p className="mt-3 italic text-lg" style={{ color: "#E6E1D2", fontFamily: "Poppins, sans-serif", fontWeight: 700, textShadow: "0 0 12px rgba(255,255,255,0.5)" }}>
            il mio influencer sei tu!
          </p>
        </div>
        <div className="max-w-4xl mx-auto mt-10 grid gap-6 md:grid-cols-3 px-4" data-testid="testimonials-section">
          <TestimonialCard name="La Sartoria Tattoo" location="Colle Val d'Elsa" stars={5} text="Conosco Riccardo Rossi da tanto tempo, e conoscendolo che in tutto quello che fa ci mette oltre il massimo, mi ha fatto piacere avere questo servizio, che ha dato una marcia in più alla mia azienda, facendo delle foto super al mio locale, foto che danno il senso di entrarci dentro, per chi è alla ricerca di qualcuno che affianchi passione a professionalità Riccardo Rossi è la persona giusta." truncate={true} />
          <TestimonialCard name="Pasticceria Mario Barone" location="Colle Val d'Elsa" stars={5} text="Persona molto professionale e seria." />
          <TestimonialCard name="Misericordia" location="Colle Val d'Elsa" stars={5} text="Ottimo lavoro e grande professionalità. Grazie" />
        </div>
        <style>{`
          @keyframes marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}</style>
      </section>

      {/* Le TUE Foto su Google — Views Reali */}
      <section className="py-14 px-6 bg-[#0c1220]" data-testid="google-views-section">
        <div className="max-w-5xl mx-auto text-center mb-8">
          <span className="inline-block px-4 py-1 rounded-full bg-[#4285F4]/10 text-[#4285F4] text-sm font-medium mb-3">
            Le tue foto su Google Maps
          </span>
          <h2 className="text-2xl sm:text-3xl text-white font-semibold">
            Casi studio <span className="text-[#D4AF37]">reali e documentati</span>
          </h2>
          <p className="text-gray-500 mt-2 max-w-xl mx-auto text-sm">Zero fuffa. Una bottiglia di vino? 250.000 visualizzazioni.</p>
          <div className="mt-3">
            <GoogleBadge size="sm" />
          </div>
        </div>
        <div className="max-w-4xl mx-auto grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {[
            { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/koil6zea_chrome_mZAu03dvZI.png", views: "886K", label: "Farmacia" },
            { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/vxjk5wi9_chrome_l5fPAeKAPr.png", views: "686K", label: "USL Toscana" },
            { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/337f114b_chrome_HaIZ6GrmIF.png", views: "664K", label: "Montaione" },
            { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/j3kybkfy_chrome_t5flV6XJXs.png", views: "262K", label: "ChiantiBanca" },
            { src: "https://customer-assets.emergentagent.com/job_07a0379a-df44-42b2-8e0e-335420c3eb92/artifacts/npw1yrx6_chrome_mrGdEQ8oEz.png", views: "245K", label: "Bottiglia vino" },
          ].map((item, i) => (
            <motion.div
              key={i}
              className="rounded-xl overflow-hidden bg-white/5 border border-white/10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <img src={item.src} alt={item.label} className="w-full h-32 object-cover" loading="lazy" />
              <div className="p-2 text-center">
                <p className="text-lg font-black text-[#D4AF37]">{item.views}</p>
                <p className="text-[10px] text-gray-500">{item.label}</p>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-8">
          <a href="https://g.page/r/CW-WqgxqiG1NEBM" target="_blank" rel="noopener noreferrer">
            <motion.span
              className="inline-flex items-center gap-1 text-[#4285F4] font-bold text-sm hover:opacity-80 transition-opacity cursor-pointer"
              whileHover={{ x: 4 }}
              data-testid="cta-menu-google"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              Scopri <ArrowRight className="w-3.5 h-3.5" />
            </motion.span>
          </a>
        </div>
      </section>

      <LogoStrip />

      {/* ═══ DONUT CHART — Servizi interattivo (allineato .com) ═══ */}
      <section className="py-24 px-6 bg-[#0c1220]">
        <div className="max-w-5xl mx-auto">
          <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-3" style={{ textShadow: "0 0 30px rgba(255,255,255,0.15)" }}>
              Cosa possiamo fare per <span className="text-[#D4AF37]">te</span>
            </h2>
            <p className="text-[#9CA3AF] text-base">Passa sopra ogni servizio per scoprire i dettagli</p>
          </motion.div>

          <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16">
            {/* Donut SVG */}
            {(() => {
              const slices = [
                { id: "start", label: "Start", pct: 20, color: "#4285F4", price: "€99", desc: "Verifica e correzione scheda", target: "start99" },
                { id: "plus", label: "Plus", pct: 20, color: "#EA4335", price: "€299", desc: "Sospensioni e recensioni", target: "pacchetti" },
                { id: "vt", label: "Virtual Tour", pct: 25, color: "#D4AF37", price: "€499", desc: "Tour 360° + foto immersive", target: "demo" },
                { id: "rooftop", label: "Rooftop", pct: 20, color: "#34A853", price: "€599", desc: "Tutto incluso + assistenza 12 mesi", target: "pacchetti" },
                { id: "formazione", label: "Formazione", pct: 15, color: "#FBBC04", price: "€299", desc: "Deducibile 100% per P.IVA", target: "pacchetti" },
              ];
              const R = 90;
              const C = 2 * Math.PI * R;
              const GAP = 4;
              let offset = 0;

              return (
                <>
                  <div className="relative flex-shrink-0" style={{ width: 260, height: 260 }}>
                    <svg viewBox="0 0 240 240" className="w-full h-full">
                      {slices.map((s) => {
                        const len = (s.pct / 100) * C;
                        const dashArray = `${len - GAP} ${C - len + GAP}`;
                        const dashOffset = -offset;
                        offset += len;
                        const isHovered = hoveredSlice === s.id;
                        return (
                          <circle
                            key={s.id}
                            cx="120" cy="120" r={R}
                            fill="none"
                            stroke={s.color}
                            strokeWidth={isHovered ? 45 : 35}
                            strokeDasharray={dashArray}
                            strokeDashoffset={dashOffset}
                            className="cursor-pointer"
                            style={{
                              transition: "all 0.3s ease",
                              filter: isHovered ? `drop-shadow(0 0 12px ${s.color}) drop-shadow(0 0 24px ${s.color})` : "none",
                              transform: isHovered ? "scale(1.05)" : "scale(1)",
                              transformOrigin: "120px 120px",
                            }}
                            onMouseEnter={() => setHoveredSlice(s.id)}
                            onMouseLeave={() => setHoveredSlice(null)}
                            onClick={() => document.getElementById(s.target)?.scrollIntoView({ behavior: "smooth" })}
                            data-testid={`donut-slice-${s.id}`}
                          />
                        );
                      })}
                    </svg>
                    {/* Centro — testo dinamico */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      {hoveredSlice ? (
                        <>
                          <span className="text-2xl font-black" style={{ color: slices.find(s => s.id === hoveredSlice)?.color, fontFamily: "Poppins, sans-serif" }}>
                            {slices.find(s => s.id === hoveredSlice)?.price}
                          </span>
                          <span className="text-white text-sm font-bold" style={{ fontFamily: "Poppins, sans-serif" }}>
                            {slices.find(s => s.id === hoveredSlice)?.label}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="text-[#9CA3AF] text-xs">da</span>
                          <span className="text-white text-2xl font-black" style={{ fontFamily: "Poppins, sans-serif" }}>€99</span>
                          <span className="text-[#9CA3AF] text-xs">3 rate PayPal</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Legenda a destra */}
                  <div className="flex-1 space-y-2 w-full max-w-md">
                    {slices.map((s) => (
                      <button
                        key={s.id}
                        onMouseEnter={() => setHoveredSlice(s.id)}
                        onMouseLeave={() => setHoveredSlice(null)}
                        onClick={() => document.getElementById(s.target)?.scrollIntoView({ behavior: "smooth" })}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-300 text-left ${hoveredSlice === s.id ? "bg-white/10 border-white/20 scale-[1.02]" : "bg-white/5 border-white/10"}`}
                        data-testid={`donut-legend-${s.id}`}
                      >
                        <div className="w-3.5 h-3.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color, boxShadow: hoveredSlice === s.id ? `0 0 10px ${s.color}` : "none" }} />
                        <div className="flex-1 min-w-0">
                          <span className="text-white text-sm font-bold">{s.label}</span>
                          <span className="text-[#9CA3AF] text-xs ml-2">{s.desc}</span>
                        </div>
                        <span className="text-sm font-black flex-shrink-0" style={{ color: s.color }}>{s.price}</span>
                      </button>
                    ))}
                    {/* Badge PayPal */}
                    <div className="flex items-center gap-2 px-4 py-2 mt-2">
                      <img src="https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_74x46.jpg" alt="PayPal" className="h-5 rounded" />
                      <span className="text-[#9CA3AF] text-xs">Tutti i pacchetti in 3 rate · TAEG 0%</span>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </section>

      <LogoStrip />

      {/* ═══ START €99 — Problema → Soluzione in 24h ═══ */}
      <section id="start99" className="py-24 px-6 bg-[#0a0e17]">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="relative p-[2px] rounded-2xl" style={{ background: "linear-gradient(135deg, #D4AF37, #FBBC05, #D4AF37)" }}>
              <div className="p-8 sm:p-10 rounded-2xl bg-[#0a0e17]">
                <div className="text-center mb-8">
                  <span className="inline-block px-4 py-1 rounded-full bg-[#EA4335]/10 text-[#EA4335] text-sm font-bold mb-4">EMERGENZA?</span>
                  <h2 className="text-3xl sm:text-4xl font-black text-white mb-2" style={{ textShadow: "0 0 30px rgba(255,255,255,0.15)" }}>
                    START <span className="text-[#D4AF37]" style={{ textShadow: "0 0 20px rgba(212,175,55,0.5)" }}>€99</span>
                  </h2>
                  <p className="text-[#9CA3AF] text-lg">Problema → Soluzione</p>
                </div>

                <div className="space-y-4 mb-8">
                  {[
                    "Profilo Google sospeso",
                    "Recensioni false da rimuovere",
                    "Scheda duplicata o bloccata",
                    "Foto rifiutate, GPS errato, orari sbagliati",
                  ].map((problem, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5">
                      <div className="w-8 h-8 rounded-full bg-[#EA4335]/20 flex items-center justify-center flex-shrink-0">
                        <Zap className="w-4 h-4 text-[#EA4335]" />
                      </div>
                      <span className="text-white text-sm font-medium">{problem}</span>
                      <ArrowRight className="w-4 h-4 text-[#34A853] ml-auto flex-shrink-0" />
                      <span className="text-[#34A853] text-sm font-bold">Risolto</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col items-center gap-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-[#FBBC05]" />
                    <span className="text-white font-bold">Presa in carico entro 24 ore</span>
                  </div>
                  <div className="inline-flex text-white font-black text-lg">
                    {"Soddisfatti o Rimborsati".split("").map((char, i) => (
                      <span key={i} className="inline-block" style={{ animation: `sb-wave 1.5s ease-in-out ${i * 0.06}s infinite` }}>
                        {char === " " ? "\u00A0" : char}
                      </span>
                    ))}
                  </div>
                  <a
                    href="https://wa.me/+393273839782?text=Ciao%20Riccardo!%20Ho%20un%20PROBLEMA%20con%20la%20mia%20scheda%20Google"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 px-8 py-3 rounded-full bg-[#D4AF37] text-black font-bold text-sm hover:bg-[#d4a518] transition-colors"
                    style={{ boxShadow: "0 0 20px rgba(212,175,55,0.3)" }}
                  >
                    START €99
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="pacchetti" className="py-20 px-6 bg-[#0c1220]">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-1 rounded-full bg-[#4285F4]/10 text-[#4285F4] text-sm font-medium mb-4">
              Detraibile 100% nel 2027
            </span>
            <h2 className="text-3xl sm:text-4xl text-white mb-4 font-semibold">
              <span className="text-[#4285F4]">Scegli</span> il tuo <span className="text-[#EA4335]">pacchetto</span>
            </h2>
            <p className="text-[#9CA3AF] max-w-xl mx-auto">
              Formazione <strong className="text-[#34A853]">100% deducibile</strong> · 3 rate PayPal TAEG 0%
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5">
            {/* START — Rosso €99 */}
            <motion.div
              onClick={() => handlePackageClick('start', 'https://wa.me/+393273839782?text=Ciao%20Riccardo!%20Voglio%20il%20Pacchetto%20START%20a%20€99')}
              className="relative rounded-2xl overflow-hidden cursor-pointer group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              data-testid="package-start"
            >
              <div className="p-5 bg-gradient-to-br from-[#EA4335] to-[#c62828] min-h-[280px] flex flex-col">
                <Play className="w-8 h-8 text-white/40 mb-3" />
                <h3 className="text-2xl font-black text-white italic mb-0.5" style={{ fontFamily: "Poppins, sans-serif" }}>Start</h3>
                <p className="text-white/60 text-xs mb-3">#DignitàDigitale</p>
                <div className="mb-3">
                  <span className="text-4xl font-black text-white">€99</span>
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <img src="https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_74x46.jpg" alt="PayPal" className="h-4 rounded" />
                  <span className="text-white/80 text-xs font-medium">oppure €33 x3 TAEG 0%</span>
                </div>
                <p className="text-white/80 text-sm mb-3 leading-snug">Verifica, claim e correzione della TUA scheda. Il primo passo per essere visibile.</p>
                <ul className="space-y-1.5 mb-4 text-sm">
                  <li className="flex items-center gap-2 text-white/90"><CheckCircle className="w-3.5 h-3.5 flex-shrink-0" /> Verifica e claim scheda</li>
                  <li className="flex items-center gap-2 text-white/90"><CheckCircle className="w-3.5 h-3.5 flex-shrink-0" /> Correzione info base</li>
                  <li className="flex items-center gap-2 text-white/90"><CheckCircle className="w-3.5 h-3.5 flex-shrink-0" /> Report iniziale</li>
                </ul>
                <div className="mt-auto flex items-center gap-1.5 text-white font-bold text-sm group-hover:translate-x-1 transition-transform" data-testid="cta-start">
                  Scopri di più <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </motion.div>

            {/* PLUS — Giallo €299 */}
            <motion.div
              onClick={() => handlePackageClick('plus', 'https://wa.me/+393273839782?text=Ciao%20Riccardo!%20Voglio%20il%20Pacchetto%20PLUS%20a%20€299')}
              className="relative rounded-2xl overflow-hidden cursor-pointer group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              data-testid="package-plus"
            >
              <div className="p-5 bg-gradient-to-br from-[#FBBC05] to-[#e5a100] min-h-[280px] flex flex-col">
                <Zap className="w-8 h-8 text-black/30 mb-3" />
                <h3 className="text-2xl font-black text-black italic mb-0.5" style={{ fontFamily: "Poppins, sans-serif" }}>Plus</h3>
                <p className="text-black/50 text-xs mb-3">Solo per i primi 10</p>
                <div className="mb-2 flex items-baseline gap-2">
                  <span className="text-lg line-through text-black/40">€399</span>
                  <span className="text-4xl font-black text-black">€299</span>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <img src="https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_74x46.jpg" alt="PayPal" className="h-4 rounded" />
                  <span className="text-black/70 text-xs font-medium">oppure €99,66 x3 TAEG 0%</span>
                </div>
                <div className="inline-flex px-3 py-1 rounded-full bg-black/10 text-black font-black text-xs mb-3 self-start">IL PIÙ VENDUTO</div>
                <p className="text-black/70 text-sm mb-3 leading-snug">Sospensione? Recensioni? Risolvo io. Il pacchetto per chi ha bisogno di assistenza concreta.</p>
                <ul className="space-y-1.5 mb-4 text-sm">
                  <li className="flex items-center gap-2 text-black/80"><CheckCircle className="w-3.5 h-3.5 flex-shrink-0" /> Recupero sospensione</li>
                  <li className="flex items-center gap-2 text-black/80"><CheckCircle className="w-3.5 h-3.5 flex-shrink-0" /> 5 foto professionali</li>
                  <li className="flex items-center gap-2 text-black/80"><CheckCircle className="w-3.5 h-3.5 flex-shrink-0" /> Consulenza 1h + report</li>
                </ul>
                <div className="mt-auto flex items-center gap-1.5 text-black font-bold text-sm group-hover:translate-x-1 transition-transform" data-testid="cta-plus">
                  Scopri di più <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </motion.div>

            {/* ROOFTOP — Verde €599 */}
            <motion.div
              onClick={() => handlePackageClick('rooftop', 'https://wa.me/+393273839782?text=Ciao%20Riccardo!%20Voglio%20il%20Pacchetto%20ROOFTOP%20a%20€599%20con%20Formazione%20IN%20REGALO')}
              className="relative rounded-2xl overflow-hidden cursor-pointer group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              data-testid="package-rooftop"
            >
              <div className="p-5 bg-gradient-to-br from-[#34A853] to-[#1b7a33] min-h-[280px] flex flex-col">
                <Star className="w-8 h-8 text-white/30 mb-3" />
                <div className="flex items-center gap-2 mb-0.5">
                  <h3 className="text-2xl font-black text-white italic" style={{ fontFamily: "Poppins, sans-serif" }}>Rooftop</h3>
                  <span className="px-2 py-0.5 rounded-full bg-white/20 text-white text-[10px] font-bold">TOP DI GAMMA</span>
                </div>
                <p className="text-white/60 text-xs mb-3">Tutto incluso, zero pensieri</p>
                <div className="mb-2">
                  <span className="text-4xl font-black text-white">€599</span>
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <img src="https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_74x46.jpg" alt="PayPal" className="h-4 rounded" />
                  <span className="text-white/80 text-xs font-medium">oppure €199,67 x3 TAEG 0%</span>
                </div>
                <p className="text-white/80 text-sm mb-3 leading-snug">Virtual Tour 360° + Assistenza premium 12 mesi. Formazione IN REGALO.</p>
                <ul className="space-y-1.5 mb-4 text-sm">
                  <li className="flex items-center gap-2 text-white/90"><CheckCircle className="w-3.5 h-3.5 flex-shrink-0" /> Tutto di START + PLUS</li>
                  <li className="flex items-center gap-2 text-white/90"><CheckCircle className="w-3.5 h-3.5 flex-shrink-0" /> Virtual Tour sbp 360°</li>
                  <li className="flex items-center gap-2 text-white/90"><CheckCircle className="w-3.5 h-3.5 flex-shrink-0" /> Formazione IN REGALO</li>
                </ul>
                <div className="mt-auto flex items-center gap-1.5 text-white font-bold text-sm group-hover:translate-x-1 transition-transform" data-testid="cta-rooftop">
                  Scopri di più <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Info deducibilità */}
          <motion.div 
            className="mt-10 p-6 rounded-2xl bg-white/5 border border-white/10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <h4 className="font-semibold text-white mb-2">Perché conviene investire ora?</h4>
                <ul className="text-base text-[#9CA3AF] space-y-1">
                  <li>✓ <strong>Paghi in 3 rate</strong> con PayPal (TAEG 0%, senza interessi)</li>
                  <li>✓ <strong>Detrai il 100%</strong> come spesa pubblicitaria nel 2027</li>
                  <li>✓ <strong>Risparmi fino al 70%</strong> rispetto ad agenzie tradizionali e fotografi professionisti</li>
                </ul>
              </div>
              <div className="flex items-center gap-4">
                <img src="https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_74x46.jpg" alt="PayPal" className="h-10" />
                <div className="text-left">
                  <p className="text-sm font-semibold text-white">Paga in 3 rate</p>
                  <p className="text-base text-[#9CA3AF]">TAEG 0% • Senza interessi</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Passaparola — Social Proof con fumetto */}

      <BundleSection />

      <section className="py-16 px-6 bg-[#0a0e17]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="grid md:grid-cols-2 gap-8 items-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Illustrazione */}
            <div className="relative">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/30a1b6ff-8b6a-4e1d-b052-200b5a3bb700/images/b84d90bfef5c96a24606d9f4a936e279e5b08fddd9d90df528e763ced8e75c66.png"
                alt="Passaparola tra imprenditori"
                className="w-full rounded-2xl border border-white/10"
                data-testid="passaparola-img"
              />
            </div>
            {/* Fumetto testuale */}
            <div className="space-y-5">
              <div className="relative p-5 rounded-2xl bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                <div className="absolute -left-3 top-6 w-0 h-0 border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent border-r-[12px] border-r-[#D4AF37]/30 hidden md:block"></div>
                <p className="text-white text-base leading-relaxed font-medium">
                  "Ho investito <span className="text-[#D4AF37] font-bold">598€</span> in formazione e pubblicità, ma il prossimo anno i <span className="text-[#D4AF37] font-bold">299€</span> della formazione sono <span className="text-[#34A853] font-bold">100% deducibili</span>. Te lo consiglio!"
                </p>
                <p className="text-[#9CA3AF] text-sm mt-2 italic">— Un tuo collega imprenditore, al bar</p>
              </div>
              <div className="p-5 rounded-2xl bg-white/5 border border-white/10">
                <p className="text-[#9CA3AF] text-sm leading-relaxed">
                  <strong className="text-white">Il passaparola funziona.</strong> Chi investe nel pacchetto <span className="text-[#FBBC05] font-bold">PLUS</span> paga <span className="text-white font-bold">3 rate da €99,67</span> con PayPal (TAEG 0%) e l'anno dopo deduce la formazione al 100%. 
                  <span className="text-[#34A853] font-bold"> Il virtual tour si ripaga da solo.</span>
                </p>
              </div>
              <a
                href="#pacchetti"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FBBC05] text-black font-bold text-sm hover:bg-[#e5ab04] transition-colors"
              >
                <Gift className="w-4 h-4" />
                Acquistali entrambi: €249
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <LogoStrip />

      {/* ═══ MAPPA — Google Maps ═══ */}
      <section className="py-16 px-6 bg-[#0c1220]" data-testid="map-section">
        <div className="max-w-4xl mx-auto">
          <motion.div className="text-center mb-8" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <a href="https://g.page/r/CW-WqgxqiG1NEBM" target="_blank" rel="noopener noreferrer" className="inline-block hover:opacity-80 transition-opacity" data-testid="map-logo-link">
              <span className="text-3xl sm:text-4xl text-[#D4AF37] font-black block" style={{ fontFamily: "Poppins, sans-serif" }}>schedabusiness.com</span>
              <span className="text-sm italic block mt-1" style={{ color: "#E6E1D2", fontFamily: "Poppins, sans-serif", fontWeight: 700 }}>se non ti trovano, non esisti</span>
            </a>
          </motion.div>
          <motion.div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl" initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
            <iframe
              src="https://maps.google.com/maps?q=schedabusiness.com+Colle+di+Val+d%27Elsa&t=&z=13&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="350"
              style={{ border: 0, filter: 'brightness(0.85) contrast(1.1)' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="schedabusiness.com su Google Maps"
              data-testid="google-map-embed"
            />
          </motion.div>
          <p className="text-center text-[#9CA3AF] text-sm mt-4">Colle di Val d'Elsa, Siena, Toscana · <a href="https://g.page/r/CW-WqgxqiG1NEBM" target="_blank" rel="noopener noreferrer" className="text-[#4285F4] hover:underline font-medium">Apri su Google Maps</a></p>
        </div>
      </section>

      {/* ═══ CONTATTI — Compatto ═══ */}
      <section className="py-20 px-6 bg-[#0a0e17]">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-6" style={{ textShadow: "0 0 30px rgba(255,255,255,0.15)" }}>
              Parliamone
            </h2>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <a href="https://wa.me/+393273839782" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#25D366] text-white font-bold text-sm hover:bg-[#20BD5A] transition-colors">
                <Phone className="w-4 h-4" /> WhatsApp
              </a>
              <a href="mailto:assistenza@schedabusiness.com" className="flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 text-white font-bold text-sm border border-white/10 hover:bg-white/20 transition-colors">
                <Mail className="w-4 h-4" /> assistenza@schedabusiness.com
              </a>
            </div>
            <p className="text-[#9CA3AF] text-sm">+39 327 383 9782 · Colle di Val d'Elsa, Siena, Toscana</p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-[#0c1220]">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col items-center gap-6">
            <div className="flex flex-col items-center gap-1">
              <a href="https://schedabusiness.com" target="_blank" rel="noopener noreferrer" className="text-3xl text-[#D4AF37] hover:opacity-80 transition-opacity" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 900 }} data-testid="logo-oro-footer">
                schedabusiness.com
              </a>
              <p className="text-sm italic" style={{ color: "#E6E1D2", fontFamily: "Poppins, sans-serif", fontWeight: 700, textShadow: "0 0 12px rgba(255,255,255,0.5)" }}>se non ti trovano, non esisti</p>
            </div>
            <p className="text-[#9AA0A6] text-sm text-center max-w-md">
              Virtual Tour 360° e Assistenza Tecnica Google Business Profile.
              <br />Riccardo Rossi — Fotografo Google Street View Certificato dal 2016.
            </p>
            <GoogleBadge size="sm" />

            <div className="flex items-center gap-4">
              <img src="https://customer-assets.emergentagent.com/job_sos-activity-cards/artifacts/wr288sey_Google-Street-View-Trusted-Badge-2021.png" alt="Google Street View Trusted" className="h-10 object-contain" />
              <img src="https://customer-assets.emergentagent.com/job_sos-activity-cards/artifacts/eknv50vh_adesivogoogle2.jpg" alt="Fotografo Google Certificato" className="h-10 object-contain rounded" />
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <a href="https://wa.me/+393273839782" target="_blank" rel="noopener noreferrer" className="text-[#34A853] hover:text-[#2D9249] transition-colors flex items-center gap-2">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <a href="mailto:assistenza@schedabusiness.com" className="text-[#4285F4] hover:text-[#1A73E8] transition-colors flex items-center gap-2">
                <Mail className="w-4 h-4" /> assistenza@schedabusiness.com
              </a>
            </div>

            <div className="flex items-center gap-4 text-base text-[#9CA3AF] mt-2">
              <span>7.000+ foto</span>
              <span className="text-[#4285F4]">•</span>
              <span>52M views</span>
              <span className="text-[#EA4335]">•</span>
              <span>Google Maps dal 2016</span>
            </div>

            <div className="w-full border-t border-white/10 mt-4 pt-4">
              <p className="text-center text-base text-[#9CA3AF]">
                © {new Date().getFullYear()} Riccardo Rossi · Consulente Business Profile Siena | Fotografo Street View Certificato
              </p>
            </div>
          </div>
        </div>
      </footer>

      <ScrollToTopButton />

      <a
        href="https://wa.me/+393273839782?text=Ciao%20Riccardo!%20Ho%20visto%20il%20sito%20e%20sono%20interessato%20al%20Virtual%20Tour%20360°"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#20BD5A] shadow-lg hover:shadow-xl transition-all flex items-center justify-center group"
        data-testid="sticky-whatsapp"
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="#000000">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
}
